import { isSelfServePlan, type PlanPrice } from "@giftforward/entitlements";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import {
  PLAN_INTERVAL_STRINGS,
  PLAN_SCHEDULED_BANNER,
  PLAN_SUMMARY_STRINGS,
} from "@/constants/Plans";
import { usePlans } from "@/hooks/usePlans";
import type { PlanTier } from "@/interfaces/tier";
import { formatDateLong } from "@/utils/date-format";
import { formatPlanPrice } from "@/utils/plan-price-helper";

/**
 * Presentational plan summary bar. The auto-renew switch is CONTROLLED —
 * checked derives from !cancelAtPeriodEnd, visual state moves only when parent's
 * plan state changes. Dismissing a confirm modal reverts the switch with no local state.
 */
interface PlanSummaryBarProps {
  plan: PlanTier;
  /**
   * What this organization pays (GET /tier `billing`): "$1,140/year" for a subscriber on the
   * retired annual price, "$95/month" for a monthly one. Null falls back to the plan's display
   * copy — Starter's "Free", Enterprise's custom pricing.
   */
  billing: PlanPrice | null;
  /** ISO date (YYYY-MM-DD) or null outside a self-serve plan. */
  renewalDate: string | null;
  /** true = downgrade scheduled: "Active until" label, ochre banner, cancel hidden, switch OFF. */
  cancelAtPeriodEnd: boolean;
  onCancel: () => void;
  onToggleAutoRenew: (autoRenew: boolean) => void;
  onKeepPlan: () => void;
  /** Disables every mutating control (impersonation / in-flight action). */
  disabled?: boolean;
}

// `uppercase` renders PLAN_SUMMARY_STRINGS labels in caps, so "Active until" and
// "RENEWS ON" look alike on screen while the constants stay PRD-exact.
const META_LABEL_CLASSES = "text-[11px] font-bold uppercase tracking-[1px] text-dust-ink";

const META_VALUE_CLASSES = "text-sm text-rodeo-black";

const BUTTON_BASE_CLASSES =
  "inline-flex items-center justify-center rounded-[6px] font-lato text-[11px] font-semibold uppercase tracking-[2px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark-dust focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-default disabled:opacity-40";
const BUTTON_OUTLINED_CLASSES =
  "border border-dark-dust/50 px-[15px] py-[5px] text-dark-dust hover:border-dark-dust hover:bg-dark-dust/5";
const BUTTON_CONTAINED_CLASSES = "border-0 bg-dark-dust px-4 py-1.5 text-white hover:bg-[#9B7D63]";

export default function PlanSummaryBar({
  plan,
  billing,
  renewalDate,
  cancelAtPeriodEnd,
  onCancel,
  onToggleAutoRenew,
  onKeepPlan,
  disabled = false,
}: PlanSummaryBarProps) {
  const { byKey } = usePlans();
  const meta = byKey(plan)?.display;
  const scheduled = cancelAtPeriodEnd;
  // Only the self-serve plans (Pro, Pro Plus) have controls; Starter and Enterprise have none.
  const hasControls = isSelfServePlan(plan);
  const planName = meta?.name ?? "";
  const dateText = formatDateLong(renewalDate);
  const autoRenewOn = !cancelAtPeriodEnd;

  return (
    <div>
      <div className="flex flex-col items-start gap-4 rounded-xl border border-smoke bg-white px-7 py-[26px] md:flex-row md:flex-wrap md:items-center md:gap-7">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-crimson text-[20px] font-normal leading-[1.2] text-rodeo-black">
            {PLAN_SUMMARY_STRINGS.title}
          </span>
          <span className="rounded-[999px] bg-good-night-4 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.05em] whitespace-nowrap text-good-night">
            {meta?.badgeLabel ?? ""}
          </span>
          <span className="text-[13px] text-graphite">
            {billing ? formatPlanPrice(billing) : (meta?.amountLabel ?? "")}
            {billing ? PLAN_INTERVAL_STRINGS.unit[billing.interval] : (meta?.unit ?? "")}
          </span>
        </div>

        {hasControls && (
          <div className="flex flex-col items-start gap-3 md:ml-auto md:flex-row md:flex-wrap md:items-center md:gap-6">
            <div className="flex items-center gap-2.5">
              <span className={META_LABEL_CLASSES}>
                {scheduled
                  ? PLAN_SUMMARY_STRINGS.activeUntilLabel
                  : PLAN_SUMMARY_STRINGS.renewsOnLabel}
              </span>
              <span className={META_VALUE_CLASSES}>{dateText}</span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                role="switch"
                aria-checked={autoRenewOn}
                aria-label={PLAN_SUMMARY_STRINGS.autoRenewLabel}
                disabled={disabled}
                onClick={() => onToggleAutoRenew(!autoRenewOn)}
                className={`relative inline-block h-5 w-10 shrink-0 rounded-full transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark-dust focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-default disabled:opacity-40 ${
                  autoRenewOn ? "bg-dark-dust" : "bg-smoke"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                    autoRenewOn ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
              <span className={META_VALUE_CLASSES}>{PLAN_SUMMARY_STRINGS.autoRenewLabel}</span>
            </div>

            {!scheduled && (
              <button
                type="button"
                onClick={onCancel}
                disabled={disabled}
                className={`${BUTTON_BASE_CLASSES} ${BUTTON_OUTLINED_CLASSES}`}
              >
                {PLAN_SUMMARY_STRINGS.cancelSubscription}
              </button>
            )}
          </div>
        )}
      </div>

      {hasControls && scheduled && (
        <div className="mt-3.5 flex flex-wrap items-start gap-3 rounded-[10px] border border-good-ochre-4 border-l-4 border-l-good-ochre bg-good-ochre-bg px-[18px] py-3.5">
          <ScheduleOutlinedIcon className="shrink-0 text-[22px] text-good-ochre" />
          <p className="min-w-[220px] flex-1 text-sm leading-normal text-rodeo-black">
            <strong className="font-bold">
              {PLAN_SCHEDULED_BANNER.strong(dateText, planName)}
            </strong>{" "}
            {PLAN_SCHEDULED_BANNER.rest(dateText, planName)}
          </p>
          <button
            type="button"
            onClick={onKeepPlan}
            disabled={disabled}
            className={`${BUTTON_BASE_CLASSES} ${BUTTON_CONTAINED_CLASSES} shrink-0 self-center`}
          >
            {PLAN_SCHEDULED_BANNER.keepButton(planName)}
          </button>
        </div>
      )}
    </div>
  );
}
